import React, { ReactNode, useState, useEffect } from 'react';
import styles from './styles.module.css';
import { Typography, Button, Divider } from 'antd';
import * as translations from './strings';
import { getLangKey } from '../../models/Language';
import { ChevronRight, GraduationCap, X } from 'lucide-react';
import { onArticleClick } from '../../utils/HelpArticleButton';
import {
  setPreferences,
  getPreference
} from '../../../../clients/sidebar/views/utils/getPreferences';
import cx from 'classnames';

const strings = translations[getLangKey()];

export enum Guide {
  WebsitesAndFiles = 'websitesAndFiles',
  IntegrationSettings = 'integrationSettings',
  InstallSettings = 'installSettings',
  ClientPermissionSettings = 'clientPermissionSettings',
  GettingStartedChecklist = 'gettingStartedChecklist',
  AISettings = 'aiSettings'
}

export type HelpGuideCardProps = {
  guide: Guide;
  bubbleContent?: ReactNode;
  guideButton?: {
    link: string;
    articleId: number;
  };
  header: string;
  message?: string;
  links?: {
    text: string;
    link: string;
    articleId: number;
  }[];
  divider?: boolean;
  isAdminView?: boolean;
  hideOnSmallScreen?: boolean;
  onHideGuide?: () => void;
};

export const HelpGuideCard = ({
  guide,
  bubbleContent = strings.help,
  message,
  guideButton,
  header,
  links,
  divider,
  isAdminView,
  hideOnSmallScreen,
  onHideGuide
}: HelpGuideCardProps) => {
  const userSetting = true;
  const [showGuide, setShowGuide] = useState<boolean>(true);
  const initShowGuide = async () => {
    const preference = await getPreference({
      userSetting,
      preference: guide,
      isAdminView
    });
    setShowGuide(preference !== false);
  };

  useEffect(() => {
    initShowGuide();
  }, []);

  useEffect(() => {
    if (!showGuide && onHideGuide) {
      onHideGuide();
    }
  }, [showGuide]);

  const handleCloseGuide = () => {
    setShowGuide(false);
    setPreferences({
      userSetting,
      preferences: { [guide]: false },
      isAdminView
    });
  };

  if (!showGuide) return null;

  return (
    <div
      className={cx(styles.helpGuides, {
        [styles.hideOnSmallScreen]: hideOnSmallScreen
      })}
    >
      <div className={styles.helpHeaderImage}>
        <span className={styles.bubble}>{bubbleContent}</span>
      </div>
      <div className={styles.helpGuideInner}>
        <Typography.Title level={3} className={styles.helpGuideHeader}>
          {header}
        </Typography.Title>
        {message && <Typography.Paragraph>{message}</Typography.Paragraph>}
        <div className={styles.guideLinks}>
          {links?.map(({ text, link, articleId }) => (
            <Button
              key={text}
              type="text"
              href={`https://support.bugherd.com${link}`}
              target="_blank"
              className={styles.helpGuideLink}
              onClick={e => {
                if (!link.includes('collections')) {
                  e?.preventDefault();
                  onArticleClick({ articleId });
                }
              }}
            >
              {text}
              <ChevronRight className={styles.chevronIcon} />
            </Button>
          ))}
        </div>
        {divider && <Divider className={styles.divider} />}
        <div className={styles.guideFooter}>
          {guideButton && (
            <Button
              type="default"
              href={`https://support.bugherd.com${guideButton.link}`}
              icon={<GraduationCap />}
              className={styles.viewGuides}
              onClick={e => {
                e?.preventDefault();
                onArticleClick({ articleId: guideButton.articleId });
              }}
            >
              {strings.readOurSupportDocs}
            </Button>
          )}
          <Button
            className={styles.closeGuideButton}
            type="default"
            icon={<X className={styles.closeIcon} />}
            onClick={handleCloseGuide}
            children={guideButton ? null : strings.close}
          />
        </div>
      </div>
    </div>
  );
};
